import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DisplayUserDto, GalleryAccessLevel, GalleryDto, TenantDto } from '@picflow/types';
import { ShareGalleryDialogComponent } from 'src/app/account/dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { DialogOptions } from 'src/app/interfaces/dialog-data.interface';
import { debounce } from 'src/app/lib/debounce';
import { AccessLevelService } from 'src/app/services/access-level.service';
import { DialogService, OnGetData } from 'src/app/services/dialog.service';
import { GalleryMetaImageUploadService } from 'src/app/services/gallery-meta-image-upload.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { StateService } from 'src/app/services/state.service';
import { InputFieldComponent } from 'src/app/shared-extra/components/input-field/input-field.component';
import { ButtonDirective } from 'src/app/shared/directives/button.directive';
import { TooltipDirective } from 'src/app/shared/directives/tooltip.directive';
import { AvatarPipe } from '../../../shared-extra/pipes/avatar.pipe';
import { RelativeTimePipe } from '../../../shared-extra/pipes/relative-time.pipe';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';
import { DeleteGalleryDialogComponent } from '../delete-gallery-dialog/delete-gallery-dialog.component';
import { MoveGalleryDialogComponent } from '../move-gallery-dialog/move-gallery-dialog.component';

@Component({
  selector: 'app-gallery-info-dialog',
  standalone: true,
  templateUrl: './gallery-info-dialog.component.html',
  styleUrls: ['./gallery-info-dialog.component.scss'],
  imports: [
    CommonModule,
    GalleryTeaserUrlPipe,
    ButtonDirective,
    SvgComponent,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    AvatarPipe,
    RelativeTimePipe,
    TooltipDirective,
  ],
})
export class GalleryInfoDialogComponent implements OnGetData {
  dialogName = 'gallery-info';
  close: (...args) => void;
  gallery: GalleryDto;
  form = new FormGroup({
    name: new FormControl(''),
  });
  accessLevel: GalleryAccessLevel;
  isGalleryGuest: boolean;
  owner: DisplayUserDto;
  inGallery: boolean;
  tenant: TenantDto;

  dialogOptions(): DialogOptions {
    return { size: 480, spacingX: 40 };
  }

  constructor(
    private galleryMetaImageUploadService: GalleryMetaImageUploadService,
    private galleryService: GalleryService,
    private dialog: DialogService,
    private accessLevelService: AccessLevelService,
    private state: StateService,
    private router: Router
  ) {}

  async onGetData(data: { gallery: GalleryDto; inGallery: boolean }, close: (...args) => void) {
    this.close = close;
    this.gallery = data.gallery;
    this.inGallery = data.inGallery;

    this.tenant = this.gallery['tenant']?.id ? this.gallery['tenant'] : this.state.activeTenant;

    const name = this.gallery.name;
    this.form.controls.name.setValue(name || '');
    this.form.valueChanges.subscribe(() => this.updateName());

    const [accessLevel, owner, isGalleryGuest] = await Promise.all([
      this.accessLevelService.getAccessLevel(this.gallery),
      this.galleryService.getGalleryOwner(this.gallery.id),
      this.accessLevelService.isGalleryGuest(this.gallery),
    ]);
    this.accessLevel = accessLevel;
    this.owner = owner;
    this.isGalleryGuest = isGalleryGuest;
  }

  async uploadTeaser(file: File) {
    await this.galleryMetaImageUploadService.upload(file, this.gallery, 'teaser');
  }

  async uploadTeaserEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    this.uploadTeaser(files[0]);
  }

  async removeTeaser() {
    this.gallery.teaser.uuid = null;
    await this.galleryService.updateGallery(this.gallery.id, { teaser: this.gallery.teaser });
  }

  private updateName = debounce(() => {
    this.gallery.name = this.form.value.name;
    this.galleryService.updateGallery(this.gallery.id, { name: this.form.value.name });
  }, 500);

  async deleteGallery() {
    await this.dialog.open(DeleteGalleryDialogComponent, { gallery: this.gallery });
    this.close();
  }

  async openMoveToDialog() {
    const resp = <{ gallery: GalleryDto; action: string }>(
      await this.dialog.open(MoveGalleryDialogComponent, { gallery: this.gallery })
    );
    if (!resp) return;
    const { action, gallery } = resp;
    if (action === 'copied') {
      if (this.inGallery) this.router.navigate(['a', 'gallery', gallery.id]);
    }
  }

  async share() {
    this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery });
  }
}
